import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import { Checkbox } from "@/components/ui/checkbox";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import {
  customReportsApi,
  type CustomReportDefinition,
  type CustomReportRunResult,
  type CustomReportSource,
} from "../../services/custom-reports.api";
import { fetchDepartments } from "@/features/announcements/services/departments.api";
import styles from "./CustomReportBuilderPage.module.scss";

const PAGE_SIZE = 20;

type PreviewRow = Record<string, unknown>;

/**
 * Kullanıcı tanımlı ("özel") rapor oluşturucu (R11).
 *
 * Platformda yalnızca sabit rapor kataloğu vardı; kullanıcı kendi kolon ve
 * filtre kombinasyonunu tanımlayamıyordu. Bu ekran whitelist'li bir veri
 * kaynağı seçtirir, kolonları işaretletir, canlı önizleme gösterir ve tanımı
 * kaydeder. Kolon/kaynak doğrulaması backend'de yapılır — burada yalnızca
 * kullanılabilir seçenekler gösterilir.
 */
const CustomReportBuilderPage: React.FC = () => {
  const { t } = useTranslation();

  const [sources, setSources] = useState<CustomReportSource[]>([]);
  const [saved, setSaved] = useState<CustomReportDefinition[]>([]);
  const [sourceKey, setSourceKey] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [page, setPage] = useState(1);

  const [preview, setPreview] = useState<CustomReportRunResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const source = useMemo(
    () => sources.find((s) => s.key === sourceKey) ?? null,
    [sources, sourceKey],
  );

  useEffect(() => {
    customReportsApi
      .listSources()
      .then((list) => {
        setSources(list);
        if (list.length > 0) setSourceKey((prev) => prev || list[0].key);
      })
      .catch(() => setSources([]));

    fetchDepartments({ limit: 100 })
      .then((list) => setDepartments(list.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setDepartments([]));

    refreshSaved();
  }, []);

  const refreshSaved = () => {
    customReportsApi
      .list()
      .then(setSaved)
      .catch(() => setSaved([]));
  };

  // Kaynak değişince önceki kaynağın kolonları geçersizdir; seçim sıfırlanır.
  useEffect(() => {
    setSelectedColumns([]);
    setSortBy("");
    setPreview(null);
    setPage(1);
  }, [sourceKey]);

  const toggleColumn = (key: string) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key],
    );
    setPage(1);
  };

  const buildDraft = useCallback(
    () => ({
      name: name.trim() || t("customReports.untitled"),
      description: description.trim() || undefined,
      dataSource: sourceKey,
      columns: selectedColumns,
      filters: {
        ...(filter.dateFrom && { dateFrom: filter.dateFrom }),
        ...(filter.dateTo && { dateTo: filter.dateTo }),
        ...(filter.departmentID && { departmentID: filter.departmentID }),
        ...(filter.search?.trim() && { search: filter.search.trim() }),
      },
      ...(sortBy && { sortBy }),
      sortOrder,
    }),
    [name, description, sourceKey, selectedColumns, filter, sortBy, sortOrder, t],
  );

  const runPreview = useCallback(async () => {
    if (!sourceKey || selectedColumns.length === 0) {
      setPreview(null);
      return;
    }
    setIsRunning(true);
    try {
      const result = await customReportsApi.preview({
        ...buildDraft(),
        page,
        limit: PAGE_SIZE,
      });
      setPreview(result);
    } catch {
      setPreview(null);
    } finally {
      setIsRunning(false);
    }
  }, [sourceKey, selectedColumns, buildDraft, page]);

  // Seçim/filtre değiştikçe önizlemeyi tazele (kısa gecikmeyle).
  useEffect(() => {
    const timer = setTimeout(() => void runPreview(), 350);
    return () => clearTimeout(timer);
  }, [runPreview]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t("customReports.errorNameRequired"));
      return;
    }
    if (selectedColumns.length === 0) {
      toast.error(t("customReports.errorColumnsRequired"));
      return;
    }
    setIsSaving(true);
    try {
      await customReportsApi.save(buildDraft());
      toast.success(t("customReports.saved"));
      refreshSaved();
    } catch {
      /* hata toast'ı interceptor tarafından gösterilir */
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoad = (definition: CustomReportDefinition) => {
    setSourceKey(definition.dataSource);
    // Kaynak değişimi seçimleri sıfırladığı için bir sonraki tick'te uygula.
    setTimeout(() => {
      setSelectedColumns(definition.columns);
      setSortBy(definition.sortBy ?? "");
      setSortOrder(definition.sortOrder);
      setName(definition.name);
      setDescription(definition.description ?? "");
      const f = definition.filters as ReportFilterState;
      setFilter({
        dateFrom: f?.dateFrom,
        dateTo: f?.dateTo,
        departmentID: f?.departmentID,
        search: f?.search,
      });
      setPage(1);
    }, 0);
  };

  const handleDelete = async (definition: CustomReportDefinition) => {
    await customReportsApi.remove(definition.id).catch(() => undefined);
    toast.success(t("customReports.deleted"));
    refreshSaved();
  };

  const previewColumns: TableColumn<PreviewRow>[] = useMemo(
    () =>
      (preview?.columns ?? []).map((col) => ({
        key: col.key,
        label: col.label,
        header: col.label,
        accessor: (row: PreviewRow) => formatCell(row[col.key], col.type),
      })),
    [preview],
  );

  // Özel rapor satırlarının doğal bir kimliği yok; tablo anahtarı için sıra
  // numarası ekleniyor (satırlar sunucudan sayfalanmış halde geliyor).
  const previewRows = useMemo(
    () => (preview?.rows ?? []).map((row, index) => ({ ...row, __rowId: index })),
    [preview],
  );

  const sortOptions = [
    { label: t("customReports.noSort"), value: "" },
    ...(source?.sortableFields ?? []).map((f) => ({
      label: source?.fields.find((x) => x.key === f)?.label ?? f,
      value: f,
    })),
  ];

  return (
    <DashboardLayout>
      <Header title={t("customReports.title")} />

      <div className={styles.page}>
        <p className={styles.intro}>{t("customReports.intro")}</p>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>1. {t("customReports.stepSource")}</h3>
          <div className={styles.row}>
            <Dropdown
              label={t("customReports.dataSource")}
              options={sources.map((s) => ({ label: s.label, value: s.key }))}
              value={sourceKey}
              selectedLabel={source?.label}
              onSelect={(v) => setSourceKey(Array.isArray(v) ? (v[0] ?? "") : v)}
            />
            {source && <span className={styles.sourceHint}>{source.description}</span>}
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>
            2. {t("customReports.stepColumns")}{" "}
            <span className={styles.counter}>
              ({selectedColumns.length}/{source?.fields.length ?? 0})
            </span>
          </h3>
          {/*
            Ortak Checkbox: tik ve label tıklaması bir arada gelir.
            (Tik artık `ui/checkbox/index.scss`'teki global kuralda çizildiği
            için ham input da doğru görünür; bu bileşen yine de tercih edilir.)
          */}
          <div className={styles.columnGrid}>
            {(source?.fields ?? []).map((field) => (
              <div key={field.key} className={styles.columnItem}>
                <Checkbox
                  value={selectedColumns.includes(field.key)}
                  label={field.label}
                  onChange={() => toggleColumn(field.key)}
                />
              </div>
            ))}
          </div>
          {selectedColumns.length === 0 && (
            <p className={styles.warning}>{t("customReports.errorColumnsRequired")}</p>
          )}
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>3. {t("customReports.stepFilters")}</h3>
          <ReportFilterBar
            value={filter}
            onChange={(next) => {
              setFilter(next);
              setPage(1);
            }}
            departments={departments}
            showDepartment={source?.supportedFilters.includes("departmentID") ?? false}
            showDateRange={
              (source?.supportedFilters.includes("dateFrom") ||
                source?.supportedFilters.includes("dateTo")) ??
              false
            }
            showSearch={source?.supportedFilters.includes("search") ?? false}
          />
          <div className={styles.row}>
            <Dropdown
              label={t("customReports.sortBy")}
              options={sortOptions}
              value={sortBy}
              selectedLabel={sortOptions.find((o) => o.value === sortBy)?.label}
              onSelect={(v) => setSortBy(Array.isArray(v) ? (v[0] ?? "") : v)}
            />
            <Dropdown
              label={t("customReports.sortOrder")}
              options={[
                { label: t("customReports.ascending"), value: "asc" },
                { label: t("customReports.descending"), value: "desc" },
              ]}
              value={sortOrder}
              selectedLabel={
                sortOrder === "asc" ? t("customReports.ascending") : t("customReports.descending")
              }
              onSelect={(v) =>
                setSortOrder((Array.isArray(v) ? v[0] : v) === "desc" ? "desc" : "asc")
              }
            />
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>4. {t("customReports.stepSave")}</h3>
          <div className={styles.row}>
            <Input
              label={t("customReports.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
            <Input
              label={t("customReports.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
            />
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSave}
              disabled={isSaving || selectedColumns.length === 0}
            >
              {t("buttons.save")}
            </button>
          </div>
        </section>

        <section className={styles.card}>
          <h3 className={styles.cardTitle}>
            {t("customReports.preview")}
            {preview && (
              <span className={styles.counter}> ({preview.total} {t("customReports.records")})</span>
            )}
          </h3>
          <Table
            columns={previewColumns}
            data={previewRows}
            isLoading={isRunning}
            getRowId={(row) => String(row.__rowId)}
            totalRecords={preview?.total ?? 0}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            emptyMessage={t("customReports.noRows")}
          />
        </section>

        {saved.length > 0 && (
          <section className={styles.card}>
            <h3 className={styles.cardTitle}>{t("customReports.savedReports")}</h3>
            <ul className={styles.savedList}>
              {saved.map((definition) => (
                <li key={definition.id} className={styles.savedItem}>
                  <div>
                    <span className={styles.savedName}>{definition.name}</span>
                    <span className={styles.savedMeta}>
                      {sources.find((s) => s.key === definition.dataSource)?.label ??
                        definition.dataSource}{" "}
                      · {definition.columns.length} {t("customReports.columnsShort")}
                    </span>
                  </div>
                  <div className={styles.savedActions}>
                    <button type="button" onClick={() => handleLoad(definition)}>
                      {t("customReports.load")}
                    </button>
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => handleDelete(definition)}
                    >
                      {t("buttons.delete")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

/** Hücre değerini türüne göre okunur biçime çevirir. */
function formatCell(value: unknown, type: string): React.ReactNode {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "boolean") return value ? "Evet" : "Hayır";
  if (type === "date") {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR");
  }
  if (type === "number") return new Intl.NumberFormat("tr-TR").format(Number(value));
  return String(value);
}

export default CustomReportBuilderPage;
